import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { usePoints } from '@/hooks/usePoints';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Trophy } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useIsMobile } from '@/hooks/use-mobile';
import { validateProfileCompletion, getFieldLabel } from '@/services/profileValidationService';
import { supabase } from '@/integrations/supabase/client';
import { internalProfileService } from '@/services/internalProfileService';

const ProfileCompletionWizard = () => {
  const navigate = useNavigate();
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser, profile: internalProfile } = useInternalAuth();
  const { checkAndAwardProfileCompletion } = usePoints();
  const isMobile = useIsMobile();

  const user = supabaseUser || internalUser;
  const currentProfile = profile || internalProfile;
  const userRole = currentProfile?.role || 'player';

  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<any>({});

  // Get required fields for the user's role
  const validationResult = validateProfileCompletion(currentProfile || {}, userRole);
  const requiredFields = validationResult.missingRequiredFields;
  const totalSteps = requiredFields.length;
  const progress = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 100;

  useEffect(() => {
    // Initialize form data with current profile
    if (currentProfile) {
      setFormData(currentProfile);
    }
  }, [currentProfile]);

  // If profile is already complete, redirect to points page
  useEffect(() => {
    if (validationResult.isComplete) {
      toast.success('Your profile is already complete!');
      navigate('/points');
    }
  }, [validationResult.isComplete, navigate]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    const currentField = requiredFields[currentStep];
    const fieldValue = formData[currentField];

    // Validate current field
    if (!fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
      toast.error(`Please fill in ${getFieldLabel(currentField)}`);
      return;
    }

    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const userId = user?.id;
      if (!userId) {
        throw new Error('User not found');
      }

      // Update profile based on user type
      if (internalUser) {
        // Separate age_group (goes to internal_users) from other profile fields (go to internal_user_profiles)
        const { age_group, ...profileFields } = formData;
        
        // Update age_group in internal_users table if provided
        if (age_group) {
          const { error: userError } = await supabase
            .from('internal_users')
            .update({ age_group })
            .eq('id', userId);
          
          if (userError) {
            console.error('Error updating age_group:', userError);
            throw userError;
          }
        }
        
        // Update other fields in internal_user_profiles table
        const result = await internalProfileService.upsertProfile({
          user_id: userId,
          ...profileFields,
        });

        if (!result.success) {
          throw new Error(result.error || 'Failed to update profile');
        }
      } else {
        const { error } = await supabase
          .from('profiles')
          .update(formData)
          .eq('id', userId);

        if (error) throw error;
      }

      // Check for profile completion bonus
      const wasAwarded = await checkAndAwardProfileCompletion(
        { ...formData, id: userId, role: userRole },
        userRole
      );

      if (wasAwarded) {
        toast.success('🎉 Profile Complete! +50 Points Earned!', {
          duration: 5000,
          description: 'Congratulations! Your profile is now 100% complete!',
        });
      } else {
        toast.success('Profile updated successfully!');
      }

      // Navigate to points page after a short delay
      setTimeout(() => {
        navigate('/points');
      }, 2000);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderFieldInput = (fieldName: string) => {
    const value = formData[fieldName] || '';

    // Sport selection fields
    if (fieldName === 'current_sport' || fieldName === 'primary_sport') {
      const sports = ['Basketball', 'Football', 'Soccer', 'Baseball', 'Tennis', 'Volleyball', 'Hockey', 'Track & Field'];
      return (
        <Select value={value} onValueChange={(val) => handleInputChange(fieldName, val)}>
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
            <SelectValue placeholder="Select a sport" />
          </SelectTrigger>
          <SelectContent>
            {sports.map((sport) => (
              <SelectItem key={sport} value={sport}>
                {sport}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    // Position selection (for players)
    if (fieldName === 'position') {
      const positions = ['Guard', 'Forward', 'Center', 'Midfielder', 'Striker', 'Defender', 'Goalkeeper'];
      return (
        <Select value={value} onValueChange={(val) => handleInputChange(fieldName, val)}>
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
            <SelectValue placeholder="Select a position" />
          </SelectTrigger>
          <SelectContent>
            {positions.map((position) => (
              <SelectItem key={position} value={position}>
                {position}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    // Skill level
    if (fieldName === 'skill_level') {
      return (
        <Select value={value} onValueChange={(val) => handleInputChange(fieldName, val)}>
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
            <SelectValue placeholder="Select skill level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="beginner">Beginner</SelectItem>
            <SelectItem value="intermediate">Intermediate</SelectItem>
            <SelectItem value="advanced">Advanced</SelectItem>
            <SelectItem value="expert">Expert</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    // Age group
    if (fieldName === 'age_group') {
      return (
        <Select value={value} onValueChange={(val) => handleInputChange(fieldName, val)}>
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
            <SelectValue placeholder="Select age group" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="13-and-under">13 and Under</SelectItem>
            <SelectItem value="14-17">14-17</SelectItem>
            <SelectItem value="18-plus">18+</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    // Coaching style
    if (fieldName === 'coaching_style') {
      return (
        <Select value={value} onValueChange={(val) => handleInputChange(fieldName, val)}>
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
            <SelectValue placeholder="Select coaching style" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="motivational">Motivational</SelectItem>
            <SelectItem value="strategic">Strategic</SelectItem>
            <SelectItem value="technical">Technical</SelectItem>
            <SelectItem value="holistic">Holistic</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    // Textarea fields
    if (fieldName.includes('description') || fieldName.includes('hours')) {
      return (
        <Textarea
          value={value}
          onChange={(e) => handleInputChange(fieldName, e.target.value)}
          placeholder={`Enter ${getFieldLabel(fieldName).toLowerCase()}`}
          className="bg-gray-700 border-gray-600 text-white min-h-[100px]"
        />
      );
    }

    // Default text input
    return (
      <Input
        type={fieldName.includes('email') ? 'email' : fieldName.includes('phone') ? 'tel' : 'text'}
        value={value}
        onChange={(e) => handleInputChange(fieldName, e.target.value)}
        placeholder={`Enter ${getFieldLabel(fieldName).toLowerCase()}`}
        className="bg-gray-700 border-gray-600 text-white"
      />
    );
  };

  if (!user || !currentProfile) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sports-green" />
      </div>
    );
  }

  if (requiredFields.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900">
        {isMobile ? <MobileHeader /> : <Header />}
        <main className="container mx-auto px-4 py-8">
          <Card className="max-w-2xl mx-auto bg-green-900/20 border-green-500">
            <CardContent className="text-center py-12">
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Profile Complete!</h2>
              <p className="text-gray-300 mb-6">Your profile is already 100% complete.</p>
              <Button onClick={() => navigate('/points')} className="bg-sports-green text-black hover:bg-green-600">
                Go to Points Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
        {isMobile ? <MobileBottomNav /> : <Footer />}
      </div>
    );
  }

  const currentField = requiredFields[currentStep];

  return (
    <div className="min-h-screen bg-gray-900">
      {isMobile ? <MobileHeader /> : <Header />}
      
      <main className="container mx-auto px-4 py-8 pb-24">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Header */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Trophy className="h-8 w-8 text-sports-green" />
              <h1 className="text-3xl font-bold text-white">Complete Your Profile</h1>
            </div>
            <p className="text-gray-400">Fill in the required fields to earn 50 points!</p>
          </div>

          {/* Progress */}
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-400">
                  <span>Step {currentStep + 1} of {totalSteps}</span>
                  <span>{Math.round(progress)}% Complete</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            </CardContent>
          </Card>

          {/* Current Field */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">{getFieldLabel(currentField)}</CardTitle>
              <CardDescription className="text-gray-400">
                This field is required to complete your {userRole} profile
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor={currentField} className="text-gray-300">
                  {getFieldLabel(currentField)} <span className="text-red-400">*</span>
                </Label>
                {renderFieldInput(currentField)}
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 0 || loading}
                  className="border-gray-600 text-gray-400 hover:bg-gray-700"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={loading}
                  className="bg-sports-green text-black hover:bg-green-600"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : currentStep === totalSteps - 1 ? (
                    <>
                      <Trophy className="mr-2 h-4 w-4" />
                      Complete & Earn Points
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Remaining Fields Preview */}
          {currentStep < totalSteps - 1 && (
            <Card className="bg-gray-800/50 border-gray-700">
              <CardContent className="pt-6">
                <p className="text-sm text-gray-400 mb-2">Remaining fields:</p>
                <div className="flex flex-wrap gap-2">
                  {requiredFields.slice(currentStep + 1).map((field) => (
                    <span key={field} className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded">
                      {getFieldLabel(field)}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {isMobile ? <MobileBottomNav /> : <Footer />}
    </div>
  );
};

export default ProfileCompletionWizard;
